import * as React from 'react'

import { cn } from '../../lib/utils'
import { KuficSkeleton } from '../kufic/kufic-skeleton'

/**
 * A loading placeholder sized by its className, so nothing shifts when the
 * data lands. It fills its box with whole rows of Kufic cells (one
 * KuficSkeleton line per 14px of height), so a block writes on the same page
 * clock as every other skeleton (loading-skeletons.md rules 2-4).
 */
function Skeleton({ className, ...props }: React.ComponentProps<'div'>) {
  const boxRef = React.useRef<HTMLDivElement>(null)
  const [rows, setRows] = React.useState(1)

  React.useLayoutEffect(() => {
    const box = boxRef.current
    if (!box) return
    const measure = () => {
      const next = Math.max(1, Math.floor((box.getBoundingClientRect().height + 4) / 14))
      setRows((current) => (current === next ? current : next))
    }
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(box)
    return () => observer.disconnect()
  }, [])

  return (
    <div
      {...props}
      ref={boxRef}
      data-slot="skeleton"
      aria-hidden="true"
      className={cn('overflow-hidden', className)}
    >
      <div className="flex flex-col gap-1">
        {Array.from({ length: rows }, (_, row) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: row position defines placeholder identity
          <KuficSkeleton key={row} width="100%" />
        ))}
      </div>
    </div>
  )
}

/** Deterministic text widths, so a skeleton draws the same on every render. */
const TEXT_WIDTHS = ['84%', '62%', '74%', '52%', '68%'] as const

/**
 * A skeleton that mirrors the real table (DESIGN.md "Data tables":
 * skeletons mirror the real column grid). Pass the same flex weights the real
 * columns use. The head is 38px over a `--border` rule, rows are
 * `--row-height` over `--border-subtle`, and the first and last cells pad by
 * `--table-edge` exactly as `Table` does, so the rows land where the skeleton
 * was. Each bar is 10px, a text line's height, at a width that varies by row
 * and column like real text does.
 *
 * The wrapper is a live region announcing "loading", so a screen-reader user
 * is told something is coming rather than being read an empty table.
 */
function SkeletonTable({
  columns,
  rows = 6,
  headers,
}: {
  columns: number[]
  rows?: number
  headers?: string[]
}) {
  return (
    <div
      data-slot="skeleton-table"
      data-skeleton-region
      role="status"
      aria-busy="true"
      className="w-full"
    >
      {headers ? (
        <div className="flex h-[38px] items-center gap-6 border-b border-border px-[var(--table-edge,0.75rem)]">
          {headers.map((header, index) => (
            <span
              // biome-ignore lint/suspicious/noArrayIndexKey: header placeholder identity is its position
              key={index}
              style={{ flex: columns[index] ?? 1 }}
              className="min-w-0 truncate text-xs font-medium text-muted-foreground"
            >
              {header}
            </span>
          ))}
        </div>
      ) : null}

      {Array.from({ length: rows }, (_, row) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: placeholder rows have no stable identity
          key={row}
          data-slot="skeleton-row"
          style={{ '--i': row } as React.CSSProperties}
          className="flex h-row items-center gap-6 border-b border-border-subtle px-[var(--table-edge,0.75rem)] last:border-b-0"
        >
          {columns.map((weight, column) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: placeholder cells have no stable identity
            <div key={column} style={{ flex: weight }} className="min-w-0">
              <KuficSkeleton width={TEXT_WIDTHS[(row + column * 2) % TEXT_WIDTHS.length]} />
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}

export { Skeleton, SkeletonTable }
